/**
 * Conversion PDP
 *
 * Drives the custom product page: option selection, price/media updates,
 * bundle-tier quantity, and the sticky buy bar.
 *
 * Add to cart is deliberately NOT handled here. The form is wrapped in the
 * theme's own <product-form-component>, which reads the variant from
 * input[name="id"] and the quantity from data-quantity-default on submit.
 * This class keeps both of those in sync so the theme's AJAX add-to-cart and
 * cart drawer keep working unchanged.
 */
class ConversionPdp extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    this.tierThumbs = this.#readJSON('[data-cpdp-tier-thumbs]') ?? {};
    if (!this.variants.length) return;

    this.optionInputs = [...this.querySelectorAll('[data-cpdp-option]')];
    this.form = this.querySelector('product-form-component');

    this.addEventListener('change', this.#onChange);

    // Anything the first render writes into must be found before that render
    // runs, or it silently no-ops and the element keeps its initial state.
    this.#initBarOptions();
    this.#initBarUpsell();

    this.#syncFromInputs();
    this.#warmTierThumbs();
    this.#initStickyBar();
    this.#initSizeGuide();
    this.#initGallery();
    this.#renderShipDates();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.observer?.disconnect();
  }

  #readJSON(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  #readVariants() {
    const node = this.querySelector('[data-cpdp-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch {
      return [];
    }
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.hasAttribute('data-cpdp-option')) this.#syncFromInputs();
    if (target.hasAttribute('data-cpdp-tier')) this.#applyTier(target);
  };

  /** Current selection, in the product's own option order. */
  #selectedOptions() {
    const byPosition = [];
    for (const input of this.optionInputs) {
      if (!input.checked) continue;
      byPosition[Number(input.dataset.optionPosition) - 1] = input.value;
    }
    return byPosition;
  }

  #syncFromInputs() {
    const selected = this.#selectedOptions();
    const variant = this.variants.find(
      (candidate) =>
        candidate.options.length === selected.length &&
        candidate.options.every((value, index) => value === selected[index])
    );

    this.#renderSelectionLabels(selected);
    this.#syncBarOptions(selected);
    this.#renderVariant(variant);
    this.#renderGallery(selected, variant);
  }

  /** The "Color: Sky Blue" / "Size: W 8" readouts above the pickers. */
  #renderSelectionLabels(selected) {
    for (const node of this.querySelectorAll('[data-cpdp-selected-for]')) {
      const value = selected[Number(node.dataset.cpdpSelectedFor) - 1];
      if (value) node.textContent = value;
    }
    this.#highlightSizeRow(selected);
  }

  /**
   * Mark the size guide row matching the current selection. Every selected
   * value is tested against the rows rather than assuming which option is the
   * size, so it works whatever order the options are in.
   */
  #highlightSizeRow(selected) {
    const rows = this.querySelectorAll('[data-size-value]');
    if (!rows.length) return;

    const chosen = new Set(selected.filter(Boolean));
    for (const row of rows) {
      row.setAttribute('data-current', String(chosen.has(row.dataset.sizeValue)));
    }
  }

  #renderVariant(variant) {
    const idInput = this.querySelector('input[name="id"]');
    const button = this.querySelector('[data-cpdp-atc]');
    const buttonLabel = this.querySelector('[data-cpdp-atc-label]');

    // No such combination exists — keep the form un-submittable rather than
    // letting it post a stale variant id.
    if (!variant) {
      if (button) button.disabled = true;
      if (buttonLabel) buttonLabel.textContent = button?.dataset.unavailableLabel ?? 'Unavailable';
      return;
    }

    if (idInput) idInput.value = variant.id;
    this.variant = variant;

    if (button) {
      button.disabled = !variant.available;
      if (buttonLabel) {
        buttonLabel.textContent = variant.available
          ? button.dataset.addLabel ?? 'Add to cart'
          : button.dataset.soldOutLabel ?? 'Sold out';
      }
    }

    this.#setText('[data-cpdp-price]', variant.price);
    this.#setText('[data-cpdp-compare]', variant.compare_at_price);
    this.#setText('[data-cpdp-bar-compare]', variant.compare_at_price);

    // The bar is driven by the tier, not the variant — see #renderTotals.
    this.#renderTotals();

    for (const node of this.querySelectorAll('[data-cpdp-compare]')) {
      node.hidden = !variant.compare_at_price;
    }

    const barSave = this.querySelector('[data-cpdp-bar-save]');
    if (barSave) {
      barSave.hidden = !variant.saving_percent;
      if (variant.saving_percent) barSave.textContent = `Save ${variant.saving_percent}%`;
    }

    const saving = this.querySelector('[data-cpdp-saving]');
    if (saving) {
      saving.hidden = !variant.saving_percent;
      if (variant.saving_percent) {
        // The amount where the shop has one, the percentage otherwise. Over
        // about $100 the amount is the larger-sounding number of the two.
        const figure = variant.saving_money ?? `${variant.saving_percent}%`;
        saving.textContent = `${saving.dataset.prefix ?? 'Save'} ${figure}`;
      }
    }

    this.#updateUrl(variant);
  }

  #setText(selector, value) {
    if (value == null) return;
    for (const node of this.querySelectorAll(selector)) node.textContent = value;
  }

  /**
   * Rebuild the gallery for the current selection: keep only media belonging
   * to the chosen colour, promote one of them into the main frame, and re-apply
   * the thumbnail limit to whatever survives.
   *
   * Media with no colour group is treated as shared and always shown, so a
   * product whose images are not tagged behaves exactly as before.
   */
  #renderGallery(selected, variant) {
    const gallery = this.querySelector('.cpdp-gallery');
    const tiles = [...this.querySelectorAll('[data-cpdp-media-id]')];
    if (!gallery || !tiles.length) return;

    const position = Number(this.dataset.colourPosition) || 0;
    const colour = position ? selected[position - 1] : null;

    // Only filter once at least one image is tagged with a colour.
    const tagged = tiles.some((tile) => tile.dataset.cpdpMediaGroup);
    const matches = tiles.filter(
      (tile) => !tagged || !colour || !tile.dataset.cpdpMediaGroup || tile.dataset.cpdpMediaGroup === colour
    );
    if (!matches.length) return;

    // Prefer the variant's own image, else the first of this colour.
    const preferred =
      matches.find((tile) => tile.dataset.cpdpMediaId === String(variant?.featured_media_id)) ?? matches[0];

    const apply = () => {
      const limit = Number(this.dataset.thumbLimit) || 4;
      let count = 0;

      for (const tile of tiles) {
        const included = matches.includes(tile);
        tile.classList.toggle('cpdp-gallery__tile--filtered', !included);

        // The promoted image is shown large, so it is not also a thumbnail.
        const isPromoted = tile === preferred;
        tile.classList.toggle('cpdp-gallery__tile--is-featured', isPromoted);

        let overflow = false;
        if (included && !isPromoted) {
          count += 1;
          overflow = count > limit;
        }
        tile.classList.toggle('cpdp-gallery__tile--overflow', overflow);
      }

      this.#promote(preferred);
      this.#renderStrip(colour);

      // The row mirrors the slides, so it hides the same pictures.
      const keep = new Set(matches.map((tile) => tile.dataset.cpdpMediaId));
      for (const thumb of this.querySelectorAll('[data-cpdp-thumb-media-id]')) {
        thumb.classList.toggle('cpdp-gallery__thumb--filtered', !keep.has(thumb.dataset.cpdpThumbMediaId));
      }

      // Nothing is rearranged here any more: the gallery is emitted with the
      // opening colour's photographs first, so they are already in place.
      if (this.#flexKnit()) {

        // The swipe belongs to the browser here, exactly as it does on the
        // other two product pages.
        //
        // It was taken over because this phone stopped short of every
        // photograph, and the reason for that was the ten photographs of other
        // colours sitting in front of the visible ones inside the reel. Taking
        // those out of the reel, which is what #restack does, fixed it: the
        // phone's own test now lands on 370, 740 and 1110 exactly. Anything
        // done here on top of that is a second hand on the wheel.
        const reel = gallery.querySelector('.cpdp-gallery__grid');
        if (reel) {
          reel.style.scrollSnapType = '';
          reel.style.touchAction = '';
          reel.style.overflowX = '';
        }
      }

      // The reel now holds a different set of slides, so it starts again.
      this.#resetGalleryScroll();
      this.#galleryThumbSync?.();
    };

    // Nothing to animate on first paint, or when the same image stays promoted.
    const featured = this.querySelector('[data-cpdp-featured-media] img');
    // Outside `apply`: that runs behind the gallery's cross-fade delay, and the
    // bundle cards have no reason to wait for an animation they are not part of.
    this.#renderTierThumbs(colour);

    const unchanged = featured?.dataset.mediaId === preferred.dataset.cpdpMediaId;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (unchanged || reduced || !this.ready) {
      apply();
      this.ready = true;
      return;
    }

    // The strip fades alongside the gallery so the column moves as one.
    const strip = this.querySelector('.cpdp__strip');
    gallery.setAttribute('data-swapping', 'true');
    strip?.setAttribute('data-swapping', 'true');

    window.setTimeout(() => {
      apply();
      gallery.setAttribute('data-swapping', 'false');
      strip?.setAttribute('data-swapping', 'false');
    }, Number(this.dataset.swapMs) || 180);
  }

  /**
   * Show only the strip images tagged for the chosen colour. Untagged images
   * are shared and always shown.
   */
  #renderStrip(colour) {
    const items = [...this.querySelectorAll('[data-cpdp-strip-group]')];
    if (!items.length || !colour) return;

    const matches = items.filter((item) => {
      const group = item.dataset.cpdpStripGroup;
      return !group || group === colour;
    });

    // Nothing tagged for this colour and nothing shared: showing an empty
    // strip would look broken, so fall back to showing everything.
    if (!matches.length) {
      for (const item of items) item.hidden = false;
      return;
    }

    for (const item of items) item.hidden = !matches.includes(item);
  }

  /**
   * Make the gallery browsable: tap a thumbnail, or swipe the main frame.
   *
   * The thumbnails previously only responded to a colour change, so tapping one
   * did nothing.
   */
  #initGallery() {
    const gallery = this.querySelector('.cpdp-gallery');
    const frame = this.querySelector('[data-cpdp-featured-media]');
    if (!gallery) return;

    gallery.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-cpdp-media-id]');
      if (tile && !tile.classList.contains('cpdp-gallery__tile--is-featured')) this.#showTile(tile);
    });

    // The reel on a phone is native scrolling. The touch handlers below still
    // serve the desktop frame, where there is no scroller to swipe.
    this.#initGalleryThumbs(this.querySelector('.cpdp-gallery__grid'));

    if (!frame) return;

    let startX = null;
    let startY = null;

    frame.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    frame.addEventListener(
      'touchend',
      (event) => {
        if (startX == null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        startX = null;

        // A mostly-vertical drag is the customer scrolling the page past the
        // image, not asking for the next one.
        if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
        this.#step(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  #initGalleryThumbs(track) {
    if (!track) return;

    const row = this.querySelector('[data-cpdp-gallery-thumbs]');
    const strip = this.querySelector('[data-cpdp-gallery-thumbs-track]');
    if (!row || !strip) return;

    const thumbs = [...strip.querySelectorAll('[data-cpdp-thumb]')];
    if (!thumbs.length) return;

    const visibleThumbs = () => thumbs.filter((t) => !t.classList.contains('cpdp-gallery__thumb--filtered'));

    // Measured once and kept. These only change when the window changes size or
    // a colour filters the row, and reading them inside a scroll handler forces
    // a style recalculation on every event.
    let geoCache = null;
    const cachedGeometry = (visible) => {
      if (!geoCache || geoCache.count !== visible.length || geoCache.view !== row.clientWidth) {
        geoCache = { ...geometry(visible), count: visible.length };
      }
      return geoCache;
    };

    /** Thumbnail width, the gap between them, and how many fit the window. */
    const geometry = (visible) => {
      const width = visible[0]?.getBoundingClientRect().width || 0;
      const gap = parseFloat(getComputedStyle(strip).gap) || 0;
      const step = width + gap;
      const view = row.clientWidth;
      return {
        width,
        gap,
        step,
        view,
        perView: step ? Math.max(1, Math.round((view + gap) / step)) : 1,
      };
    };

    // The photograph a tap is travelling towards. While the reel animates it
    // passes through every picture in between, and each of those would
    // otherwise reposition the strip, undoing the paging the tap just did.
    let travellingTo = null;

    row.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-cpdp-thumb]');
      if (!thumb) return;

      const visible = visibleThumbs();
      const position = visible.indexOf(thumb);
      if (position < 0) return;

      travellingTo = position;
      closeRoom();
      track.scrollTo({ left: position * track.clientWidth, behavior: 'smooth' });

      // Tapping the thumbnail at either end pages the strip, so the pictures
      // beyond it come into view. Without this the tapped one stays pinned to
      // the edge it was already on and nothing new is revealed.
      //
      // Under the timeline the reel's new position re-windows the strip by
      // itself, and paging it here as well would move it twice.
      if (compositorDriven) return;

      const geo = geometry(visible);
      if (!geo.step || !geo.view) return;

      const firstVisible = Math.round(row.scrollLeft / geo.step);
      const lastVisible = firstVisible + geo.perView - 1;

      if (position <= firstVisible) {
        row.scrollTo({ left: (position - (geo.perView - 1)) * geo.step, behavior: 'smooth' });
      } else if (position >= lastVisible) {
        row.scrollTo({ left: position * geo.step, behavior: 'smooth' });
      }
    });

    /** Which thumbnail currently carries the highlight. */
    let marked = -1;

    /** Width of the padding in front of the strip, and its resting position. */
    let stripTravel = null;

    /** The row's own width, which is what four thumbnails across is measured on. */
    let stripView = null;

    /** Where along the reel the strip begins to move. */
    let stripStart = null;

    // A finger can be lifted anywhere, including out in the empty room in front
    // of the strip. This eases it back to the first thumbnail, and only ever
    // after the gesture has finished, so nothing interferes with the scrolling
    // itself. The far end needs no equivalent: the shift shortens the range
    // from that side, so the browser holds the position in bounds by itself.
    let settle = null;
    const shiftNow = () => {
      const matrix = new DOMMatrixReadOnly(getComputedStyle(strip).transform);
      return Math.abs(matrix.m41);
    };

    /** How much room is currently open behind the strip. */
    let lead = 0;

    /**
     * Opens exactly as much room behind the strip as the shift has taken, so
     * the browser's own limit is the first thumbnail rather than the far end of
     * a fixed lead. Nothing moves: the room and the resting position are the
     * same number, and the strip sits at the difference between them.
     *
     * Called as a finger lands, which is both the moment it matters and a
     * moment when the reel is not moving, so the measurement holds for the
     * whole gesture.
     */
    // The one product where the two writes below are checked afterwards.
    const checked = this.#flexKnit();

    /**
     * Puts the strip at the far end of its room, and makes sure it got there.
     *
     * The room and the position have to agree. If the position is written
     * before the browser has applied the room, it is held to the range as it
     * was and lands short, and nothing afterwards puts it right: the room says
     * one thing and the strip sits somewhere else. That mismatch is both of
     * the faults reported, a strip showing its first photographs while the
     * main image is on its last, and white space to the left of it when it is
     * dragged.
     *
     * Reading the width first is supposed to apply the room. Where that is not
     * enough, the position is simply written again on the next frame, and once
     * more after that. Nothing is being dragged at this point, so a frame
     * costs nothing.
     */
    const park = (shift, tries) => {
      row.scrollLeft = shift;

      if (!checked) return;
      if (Math.abs(row.scrollLeft - shift) <= 1 || tries >= 2) return;

      window.requestAnimationFrame(() => park(shift, tries + 1));
    };

    /**
     * Takes the room away without the strip appearing to move.
     *
     * The room and the scroll position are set together, so the strip sits at
     * the difference between them and that difference is kept. Used when a tap
     * sends the reel elsewhere: the room belongs to where the reel was, and
     * leaving it open during the journey shows as white space beside the boxes.
     */
    const closeRoom = () => {
      if (!compositorDriven || lead === 0) return;

      const showing = row.scrollLeft - lead;
      lead = 0;
      row.style.setProperty('--cpdp-strip-lead', '0px');
      void row.scrollWidth;
      row.scrollLeft = Math.max(0, showing);
    };

    const openRoom = () => {
      if (!compositorDriven) return;

      const shift = Math.round(shiftNow());
      if (shift === lead) return;
      lead = shift;

      row.style.setProperty('--cpdp-strip-lead', `${shift}px`);

      // Reading the width applies the room above before the position below is
      // measured against it.
      void row.scrollWidth;
      park(shift, 0);
    };

    row.addEventListener('pointerdown', openRoom, { passive: true });
    row.addEventListener('touchstart', openRoom, { passive: true });

    row.addEventListener(
      'scroll',
      () => {
        if (!compositorDriven) return;
        clearTimeout(settle);
        settle = setTimeout(() => {
          // Measured against the room actually in force rather than the
          // number we believe we wrote, so a write that did not take cannot
          // leave the strip sitting in empty space with nothing to correct it.
          const room = checked
            ? parseFloat(getComputedStyle(strip).paddingInlineStart) || 0
            : lead;
          const floor = room - shiftNow();
          if (row.scrollLeft < floor - 1) row.scrollTo({ left: floor, behavior: 'smooth' });
        }, 140);
      },
      { passive: true }
    );

    // Eased towards the reel rather than pinned to it exactly.
    //
    // Scroll events do not arrive on every frame, especially while a phone is
    // carrying momentum, so writing the reel's position straight across left
    // the strip advancing in visible steps. Easing a fraction of the remaining
    // distance each frame fills those gaps, while staying close enough that it
    // never reads as trailing behind.
    // No easing between the reel and the strip.
    //
    // Every softened version of this trailed the finger, because that is what
    // easing is: it holds back a fraction of the distance on purpose. Written
    // straight across, the strip's position is a direct function of where the
    // reel is, so it moves in the same frame the finger does and stops when the
    // finger stops. The reel's own scrolling supplies the smoothness.
    let position = row.scrollLeft;

    // Where the browser can tie the strip to the reel itself, it does, and
    // nothing below positions the strip at all. What remains here marks the
    // current thumbnail, handles taps, and hands the stylesheet the two
    // measurements it cannot work out on its own.
    //
    // This is the whole fix. Script can only move the strip as often as scroll
    // events arrive, and a phone withholds them while it carries momentum,
    // which is precisely when the strip has to move. The compositor is under no
    // such limit: it re-evaluates the strip's position every frame, in the same
    // place and at the same time as the reel the finger is dragging.
    const compositorDriven =
      typeof CSS !== 'undefined' && CSS.supports?.('animation-timeline: scroll()');

    // Older browsers only, where there is no timeline to hand it to. Straight
    // across, every scroll event, with nothing in between.
    const glideTo = (value) => {
      if (compositorDriven) return;
      if (Math.abs(value - position) < 0.5) return;
      position = value;
      row.scrollLeft = value;
    };

    /**
     * The two numbers the stylesheet cannot derive: how far the strip has to
     * travel in total, and how far along the reel it starts.
     */
    const describeStrip = (visible, geo) => {
      if (!geo.step) return;

      const travel = Math.max(0, visible.length * geo.step - geo.gap - geo.view);

      // What four thumbnails across means, kept clear of the padding below.
      if (geo.view !== stripView) {
        stripView = geo.view;
        row.style.setProperty('--cpdp-strip-view', `${geo.view}px`);
      }

      // Only when the measurement itself changes: a new colour, a resize. The
      // reel moving does not change it, and writing the scroll position on
      // every scroll event would take the strip away from the finger.
      if (travel !== stripTravel) {
        stripTravel = travel;
        row.style.setProperty('--cpdp-strip-travel', `${travel}px`);
      }

      // The reel's timeline runs 0 to 100% across all its photographs, so the
      // strip sets off the fraction of the way along at which the last
      // thumbnail on show is reached, and holds still before that.
      //
      // Written only when it changes. It was being written on every scroll
      // event the reel produced, which is many times a second while a finger
      // is on it, and each write invalidated style for everything inside the
      // gallery. That included the reel, mid-settle, which is what stopped it
      // short of the photograph and left the edge of the next one showing.
      const start = visible.length > 1 ? ((geo.perView - 1) / (visible.length - 1)) * 100 : 100;
      const from = `${Math.min(100, start).toFixed(3)}%`;
      if (from !== stripStart) {
        stripStart = from;
        row.style.setProperty('--cpdp-strip-start', from);
      }
    };

    const sync = () => {
      const visible = visibleThumbs();
      if (!visible.length || !track.clientWidth) return;

      const active = Math.min(visible.length - 1, Math.round(track.scrollLeft / track.clientWidth));

      // Only when it actually changes. Rewriting these every frame invalidated
      // style for the whole row on each one, for no visible difference.
      if (active !== marked) {
        marked = active;
        visible.forEach((thumb, index) => {
          thumb.classList.toggle('cpdp-gallery__thumb--active', index === active);
          thumb.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      }

      // Mid-journey after a tap: keep the highlight current, but leave the
      // strip where the tap put it. Normal behaviour resumes on arrival.
      if (travellingTo !== null) {
        if (active !== travellingTo) return;
        travellingTo = null;

        // The room behind the strip belongs to wherever the reel was before
        // the tap. Arriving somewhere else leaves it open with nothing in it,
        // which shows as white space to the left of the boxes until the next
        // thing happens to correct it. This closes it on arrival.
        window.requestAnimationFrame(openRoom);
      }

      const geo = cachedGeometry(visible);
      if (!geo.width) return;

      describeStrip(visible, geo);

      // Driven by where the reel actually is, not by which photograph it has
      // most recently become. Waiting for that meant the strip only set off
      // once the swipe was half done, and then ran its own animation to catch
      // up, which is what felt like lag.
      //
      // The fractional position keeps the strip moving in step with the finger
      // once it has something to do, and pinned at the start until then: while
      // the current photograph is one of the first four, this is zero or less.
      const exact = track.scrollLeft / track.clientWidth;
      const maxOffset = Math.max(0, visible.length * geo.step - geo.gap - geo.view);

      // Where the strip would have to be for this photograph to be the last
      // thumbnail in the window, and where for it to be the first. Between
      // those two the thumbnail is already on screen and nothing should move.
      //
      // Applied in both directions, which is the point: coming back from the
      // end, the strip holds until the current photograph reaches the leftmost
      // thumbnail, rather than unwinding from the moment the swipe begins.
      //
      // Taken from the reel's exact position, fractions included, so the moment
      // the photograph starts to leave the window the strip starts to move with
      // it. An earlier version waited until the reel had committed to the next
      // photograph before moving at all, which is what made the strip sit still
      // through most of a swipe and then lurch.
      const asLast = exact * geo.step + geo.width - geo.view;
      const asFirst = exact * geo.step;
      const wanted = Math.min(Math.max(position, asLast), asFirst);

      const target = Math.max(0, Math.min(wanted, maxOffset));

      glideTo(target);
    };

    track.addEventListener('scroll', sync, { passive: true });
    this.#snapCheck(track);
    this.#bareStage();


    // And once the reel has properly stopped, for anything that reaches the
    // strip without a finger landing on it first.
    //
    // Well clear of the settle. At 180ms this was landing inside it, and the
    // work it does, a property written and a scroll position set, was enough
    // to stop the reel where it stood.
    let reelStill = null;
    track.addEventListener(
      'scroll',
      () => {
        clearTimeout(reelStill);
        reelStill = setTimeout(openRoom, 700);
      },
      { passive: true }
    );
    window.addEventListener('resize', () => { geoCache = null; sync(); }, { passive: true });
    this.#galleryThumbSync = sync;
    sync();
  }

  /**
   * True only on the Flex Knit product, and on nothing else in the shop.
   *
   * Read from the product's own address, so the two other product pages cannot
   * reach the code below however their colours are arranged.
   */
  #flexKnit() {
    return /\/kindfeet-flex-knit(?:[/?#]|$)/.test(this.dataset.productUrl || '');
  }

  /**
   * Takes the other colours' photographs out of the reel instead of hiding
   * them inside it.
   *
   * Flex Knit's colour is the last one, so ten hidden photographs sat in front
   * of the five on show. Hidden or not, Safari went on treating the reel as
   * though they were there, and snapped to marks the photographs no longer
   * occupied: every swipe stopped a fraction short and left a sliver behind.
   *
   * Removing them entirely leaves a reel that begins with its first visible
   * photograph, which is exactly the shape of the other two product pages. The
   * browser's own snapping then does the swipe, so it behaves and feels the
   * same as those pages rather than being handled differently here.
   *
   * They are kept, not destroyed: a colour change puts them back in their
   * original order.
   */
  #restack(gallery, tiles, matches) {
    const reel = gallery.querySelector('.cpdp-gallery__grid');
    if (!reel) return;

    // Their order as the page was built, before any of this moves them.
    tiles.forEach((tile, index) => {
      if (!tile.dataset.cpdpOrder) tile.dataset.cpdpOrder = String(index);
    });

    const ordered = [...tiles].sort(
      (a, b) => Number(a.dataset.cpdpOrder) - Number(b.dataset.cpdpOrder)
    );

    // Visible photographs first, the other colours' behind them, all of them
    // still in the reel.
    //
    // This is the arrangement the two product pages that behave correctly
    // already have, because their colour happens to be the first one. Flex
    // Knit's is the last, so its visible photographs sat behind ten hidden
    // ones, and an earlier fix took those out of the reel altogether, which is
    // a third arrangement matching neither. Every measurable property of this
    // gallery is identical to theirs; this was the last thing that was not.
    for (const tile of ordered) {
      tile.style.scrollSnapAlign = '';
      if (matches.includes(tile)) reel.appendChild(tile);
    }

    for (const tile of ordered) {
      if (!matches.includes(tile)) reel.appendChild(tile);
    }

    // Nothing is kept outside the reel any more.
    const store = gallery.querySelector('[data-cpdp-media-store]');
    if (store) store.remove();

    // The reel's layout is thrown away and built again.
    //
    // Everything gentler has failed: asking for the snapping to be worked out
    // again, changing how the photographs align, pinning the spacing. Safari
    // goes on landing a fixed distance short, which is the mark of
    // measurements taken before these photographs were moved and held on to
    // afterwards. Taking the reel out of the layout entirely and putting it
    // back leaves none of them to hold.
    const rebuild = () => {
      const at = reel.scrollLeft;
      reel.style.display = 'none';
      void reel.offsetWidth;
      reel.style.display = '';
      reel.scrollLeft = at;
    };

    rebuild();

    // And again as the page settles: images arriving, fonts swapping and the
    // header collapsing all change the frame, and each of those is a chance
    // for the reel to be knocked off the mark. All of this happens before
    // anyone has had time to swipe.
    if (!this.#snapSettled) {
      this.#snapSettled = true;

      const settle = () => rebuild();

      if (document.readyState === 'complete') window.requestAnimationFrame(settle);
      else window.addEventListener('load', () => window.requestAnimationFrame(settle), { once: true });
    }
  }

  /** Set once the snap positions have been reworked after loading. */
  #snapSettled = false;

  /** Set once the correction is listening. */
  #netCast = false;

  /**
   * Everything but the gallery taken off the page, for one experiment.
   *
   * This product's page carries a different set of sections to the two that
   * behave, and a page's other contents can affect how a phone scrolls. With
   * them gone, a swipe that still stops short rules the rest of the page out
   * entirely, and one that lands cleanly names the culprit.
   *
   * Only when the address carries ?bare=1, so no customer meets it.
   */
  #bareStage() {
    const asked = new URLSearchParams(window.location.search);
    const bare = asked.get('bare');
    if (!bare) return;

    const mine = this.closest('.shopify-section');
    const all = [...document.querySelectorAll('.shopify-section')];

    // ?bare=1 strips everything. ?bare=2,5 keeps only those, counted from the
    // top of the page as they appear, with the gallery always kept.
    const keep = bare === '1' ? [] : bare.split(',').map((n) => Number(n.trim()) - 1);

    all.forEach((section, index) => {
      if (section === mine) return;
      if (!keep.includes(index)) section.style.display = 'none';
    });
  }

  /**
   * A self test, run on the customer's own phone and printed on the page.
   *
   * The fault only happens on a phone, and a phone cannot be watched from
   * here. So the page does the measuring itself: it asks the browser where it
   * lands with its own snapping in force, records where real swipes come to
   * rest, and prints both. One screenshot then carries everything that hours
   * of guessing could not establish.
   *
   * Off unless the address carries ?snapcheck=1, so no customer meets it.
   */
  #snapCheck(reel) {
    if (!/snapcheck/.test(window.location.search)) return;

    const panel = document.createElement('div');
    panel.style.cssText =
      'position:fixed;left:6px;right:6px;top:6px;z-index:2147483647;background:#000;color:#4AFF7F;' +
      'font:13px/1.5 ui-monospace,Menlo,monospace;padding:10px;border-radius:10px;white-space:pre-wrap;' +
      'pointer-events:none;box-shadow:0 2px 12px rgba(0,0,0,0.5)';
    (document.body || document.documentElement).appendChild(panel);

    const lines = [];
    const draw = () => {
      panel.textContent = lines.join('\n');
    };

    const width = () => reel.clientWidth || 1;

    // What the browser does with its own snapping, which is what the other two
    // product pages rely on. Restored exactly as it was afterwards.
    const askBrowser = async () => {
      const was = reel.style.scrollSnapType;
      const here = reel.scrollLeft;
      const w = width();

      reel.style.scrollSnapType = 'x mandatory';
      void reel.offsetWidth;

      const landings = [];
      for (let i = 1; i <= 3; i += 1) {
        reel.scrollLeft = i * w;
        await new Promise((go) => window.setTimeout(go, 350));
        const at = Math.round(reel.scrollLeft);
        landings.push(`${i * w}->${at}${at === i * w ? '' : ' (' + (at - i * w) + ')'}`);
      }

      reel.style.scrollSnapType = was;
      reel.scrollLeft = here;
      return landings;
    };

    // Every swipe the customer makes, in both directions, with the photograph
    // it landed on and how far off the mark it stopped.
    const swipes = [];
    let last = null;
    let wrong = 0;
    let settle = null;

    reel.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(settle);
        settle = window.setTimeout(() => {
          const w = width();
          const at = Math.round(reel.scrollLeft);
          if (at === last) return;

          const photo = Math.round(at / w) + 1;
          const past = at % w;
          const adrift = past < w - past ? -past : w - past;
          const way = last === null || at > last ? '>' : '<';

          if (Math.abs(adrift) > 1) wrong += 1;
          last = at;

          swipes.push(`${way}${photo}${Math.abs(adrift) > 1 ? '[' + adrift + ']' : ''}`);
          if (swipes.length > 14) swipes.shift();

          lines[4] = `swipes (${wrong} off): ` + swipes.join(' ');
          draw();
        }, 220);
      },
      { passive: true }
    );

    window.setTimeout(async () => {
      const tile = reel.querySelector('[data-cpdp-media-id]');
      const ts = tile ? window.getComputedStyle(tile) : null;
      const cs = window.getComputedStyle(reel);

      lines[0] = `frame ${width()}  photo ${tile ? tile.getBoundingClientRect().width.toFixed(1) : '-'}  count ${reel.querySelectorAll('[data-cpdp-media-id]').length}`;
      lines[1] = `align ${ts ? ts.scrollSnapAlign : '-'}  margin ${ts ? ts.scrollMarginLeft : '-'}  pad ${cs.scrollPaddingLeft}`;
      lines[2] = `timeline ${window.CSS && CSS.supports('animation-timeline: scroll()') ? 'yes' : 'no'}  live snap ${cs.scrollSnapType}`;
      lines[3] = 'browser test: running…';
      lines[4] = 'swipes (0 off): swipe through every photo, then back';
      draw();

      const landings = await askBrowser();
      lines[3] = 'browser test: ' + landings.join('  ');
      draw();
    }, 1200);
  }

  /** Tiles the current colour selection leaves on screen. */
  #browsableTiles() {
    return [...this.querySelectorAll('[data-cpdp-media-id]')].filter(
      (tile) => !tile.classList.contains('cpdp-gallery__tile--filtered')
    );
  }

  #step(direction) {
    const tiles = this.#browsableTiles();
    if (tiles.length < 2) return;

    const current = tiles.findIndex((tile) => tile.classList.contains('cpdp-gallery__tile--is-featured'));
    // Wraps, so a swipe never dead-ends on the first or last image.
    this.#showTile(tiles[(current + direction + tiles.length) % tiles.length]);
  }

  /**
   * Promote a tile and re-run the thumbnail bookkeeping, mirroring what a
   * colour change does: the promoted tile is hidden from the row and does not
   * count against the thumbnail limit, so the row always stays full.
   */
  /** Set by #initGalleryThumbs once the row exists. */
  #galleryThumbSync = null;

  /** Back to the first photograph of the colour just chosen. */
  #resetGalleryScroll() {
    const track = this.querySelector('.cpdp-gallery__grid');
    if (!track || track.scrollWidth <= track.clientWidth) return;
    track.scrollTo({ left: 0, behavior: 'smooth' });
  }

  #showTile(tile) {
    const limit = Number(this.dataset.thumbLimit) || 4;
    let count = 0;

    for (const candidate of this.querySelectorAll('[data-cpdp-media-id]')) {
      const included = !candidate.classList.contains('cpdp-gallery__tile--filtered');
      const isPromoted = candidate === tile;
      candidate.classList.toggle('cpdp-gallery__tile--is-featured', isPromoted);

      let overflow = false;
      if (included && !isPromoted) {
        count += 1;
        overflow = count > limit;
      }
      candidate.classList.toggle('cpdp-gallery__tile--overflow', overflow);
    }

    this.#promote(tile);
  }

  /**
   * Fetch every colour's bundle photo once the page is idle.
   *
   * Only the opening colour is in the markup, so without this the first switch
   * pays for a download before anything changes on screen. Deferred to idle so
   * it never competes with the initial render.
   */
  #warmTierThumbs() {
    const urls = Object.values(this.tierThumbs ?? {});
    if (urls.length < 2) return;

    const warm = () => {
      for (const url of urls) {
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
      }
    };

    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 2500 });
    else window.setTimeout(warm, 1200);
  }

  /**
   * Bundle cards track the chosen colour from their own lookup, never from the
   * gallery — the gallery holds the studio shot, which would paint a background
   * back over a transparent cut-out.
   */
  #renderTierThumbs(colour) {
    const source = colour ? this.tierThumbs?.[colour] : null;
    if (!source) return;

    for (const thumb of this.querySelectorAll('[data-cpdp-tier-thumb]')) {
      if (!thumb.src.endsWith(source)) thumb.src = source;
    }
  }

  /** Copy a tile's image into the main frame. */
  #promote(tile) {
    const featured = this.querySelector('[data-cpdp-featured-media] img');
    const source = tile?.querySelector('img');
    if (!featured || !source) return;

    // The sticky bar's thumbnail rides along with the gallery, so it follows a
    // colour change without needing its own lookup.
    //
    // The gallery's second photograph rather than the promoted one. The
    // promoted one is often the cut-out the bundle cards use, which has no
    // background and reads as a mistake at thumbnail size, and the second is
    // the clean product shot on every product here.
    const barThumb = this.querySelector('[data-cpdp-bar-thumb]');
    if (barThumb && !this.querySelector('conversion-bundle')) {
      const shown = [...this.querySelectorAll('.cpdp-gallery__grid [data-cpdp-media-id]')].filter(
        (el) => !el.classList.contains('cpdp-gallery__tile--filtered')
      );
      const pick = (shown[1] ?? shown[0])?.querySelector('img');
      barThumb.src = pick?.currentSrc || pick?.src || source.src;
    }


    featured.src = source.src;
    featured.srcset = source.srcset;
    featured.alt = source.alt;
    featured.dataset.mediaId = tile.dataset.cpdpMediaId;
  }

  #updateUrl(variant) {
    if (!this.dataset.productUrl) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Bundle tiers add multiples of the same variant. The theme's form reads
   * quantity from data-quantity-default at submit time, so setting it here is
   * enough — no hidden quantity input is involved.
   */
  #applyTier(input) {
    if (this.form) this.form.dataset.quantityDefault = input.value;
    this.#renderTotals();
  }

  /**
   * The sticky bar has to show what the customer is about to pay, which is the
   * selected bundle's total rather than the price of one pair. Reading the
   * checked tier rather than the tier that was just clicked means a variant
   * change re-renders the same figure instead of reverting to the unit price.
   */
  #renderTotals() {
    const tier = this.querySelector('[data-cpdp-tier]:checked');
    const total = tier?.dataset.total ?? this.variant?.price;

    if (total != null) {
      this.#setText('[data-cpdp-bar-price]', total);
      this.#setText('[data-cpdp-atc-total]', total);
    }

    // Struck only when the bundle actually saves something; on a single pair
    // the two figures are identical and showing both reads as an error.
    const was = this.querySelector('[data-cpdp-atc-was]');
    if (was) {
      const regular = tier?.dataset.regular;
      was.textContent = regular && regular !== total ? regular : '';
    }

    this.#renderBarUpsell(tier);

    // Colour, size and the chosen bundle, for a customer who has scrolled past
    // the pickers and can no longer see what is selected.
    //
    // When the bundle builder is present it owns this line and the thumbnail
    // stack, because a bundle can hold shoes this element knows nothing about.
    // Single owner, so the two cannot overwrite each other.
    const meta = this.querySelector('[data-cpdp-bar-meta]');
    if (meta && !this.querySelector('conversion-bundle')) {
      const parts = this.#selectedOptions().filter(Boolean);
      if (tier?.dataset.title) parts.push(tier.dataset.title);
      meta.textContent = parts.join(' \u00b7 ');
    }
  }

  /**
   * Size guide drawer. Built on <dialog>, so focus trapping and Esc-to-close
   * come from the platform — this only wires the open and close triggers.
   */
  #initSizeGuide() {
    const dialog = this.querySelector('[data-cpdp-size-guide]');
    if (!dialog) return;

    for (const opener of this.querySelectorAll('[data-cpdp-size-guide-open]')) {
      opener.addEventListener('click', (event) => {
        event.preventDefault();
        dialog.showModal();
      });
    }

    for (const closer of dialog.querySelectorAll('[data-cpdp-size-guide-close]')) {
      closer.addEventListener('click', () => dialog.close());
    }

    // Clicks land on the dialog itself only when they hit the backdrop, since
    // the panel fills it. Deliberate dismissal, never an accidental one.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  /**
   * The sticky bar's selects drive the same radios the pickers up the page use,
   * rather than keeping a second copy of the selection. One source of truth, so
   * the two can never disagree.
   */
  #initBarOptions() {
    const selects = [...this.querySelectorAll('[data-cpdp-bar-option]')];
    if (!selects.length) return;

    for (const select of selects) {
      select.addEventListener('change', () => {
        const position = select.dataset.cpdpBarOption;
        const radio = this.optionInputs.find(
          (input) => input.dataset.optionPosition === position && input.value === select.value
        );
        if (!radio || radio.disabled) return;

        radio.checked = true;
        // Bubbles to this element's own change handler, which re-runs the
        // whole variant render exactly as a swatch click would.
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    this.barSelects = selects;
  }

  /** One tap moves the customer up to the next bundle. */
  #initBarUpsell() {
    const button = this.querySelector('[data-cpdp-bar-upsell]');
    if (!button) return;

    button.addEventListener('click', () => {
      const id = button.dataset.tierId;
      const tier = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
      if (!tier || tier.checked) return;

      tier.checked = true;
      tier.dispatchEvent(new Event('change', { bubbles: true }));

      // Selecting from a bar pinned to the top changes something the customer
      // cannot see, so take them to it. Deferred a frame so the change has
      // settled before the position is measured.
      requestAnimationFrame(() => this.#scrollToTiers());
    });

    this.barUpsell = button;
  }

  /** Bring the bundle cards to rest just under the site header. */
  #scrollToTiers() {
    const tiers = this.querySelector('.cpdp__tiers');
    if (!tiers) return;

    const headerHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;

    // The "most popular" ribbon hangs above the card, so the gap has to clear
    // the header by more than the card's own edge.
    const clearance = headerHeight + 22;
    const top = tiers.getBoundingClientRect().top + window.scrollY - clearance;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  /**
   * Offer the next bundle up from whatever is selected.
   *
   * Wording and figures come from the tier itself rather than being written
   * here, so the nudge cannot promise a saving the cards do not show.
   */
  #renderBarUpsell(current) {
    const button = this.barUpsell;
    if (!button) return;

    const quantity = Number(current?.value) || 0;
    const next = [...this.querySelectorAll('[data-cpdp-tier]')]
      .map((tier) => ({ tier, qty: Number(tier.value) }))
      .filter((entry) => entry.qty > quantity && entry.tier.dataset.savingText)
      .sort((a, b) => a.qty - b.qty)[0];

    if (!next) {
      button.hidden = true;
      return;
    }

    button.hidden = false;
    button.dataset.tierId = next.tier.id;
    this.#setText(
      '[data-cpdp-bar-upsell-text]',
      `${next.tier.dataset.title ?? `${next.qty} pairs`} for ${next.tier.dataset.total} — save ${next.tier.dataset.savingText}`
    );
  }

  /** Mirror the current selection back into the bar's selects. */
  #syncBarOptions(selected) {
    for (const select of this.barSelects ?? []) {
      const value = selected[Number(select.dataset.cpdpBarOption) - 1];
      if (value && select.value !== value) select.value = value;
    }
  }

  /**
   * Turn [date] placeholders into a real shipping date.
   *
   * Computed in the browser rather than in Liquid on purpose: Shopify caches
   * rendered pages, so a server-side date can be served stale — and it would
   * be the shop's date, not the visitor's.
   */
  #renderShipDates() {
    const nodes = this.querySelectorAll('[data-cpdp-ship-date]');
    if (!nodes.length) return;

    for (const node of nodes) {
      const days = Number(node.dataset.shipDays);
      const target = new Date();
      let added = 0;

      // Stepped a day at a time rather than offset in one go, because how many
      // calendar days a business day spans depends where the weekend falls
      // relative to today.
      while (added < days) {
        target.setDate(target.getDate() + 1);
        const weekday = target.getDay();
        if (weekday !== 0 && weekday !== 6) added += 1;
      }

      // Never name a weekend, even at zero days ahead.
      while (target.getDay() === 0 || target.getDay() === 6) {
        target.setDate(target.getDate() + 1);
      }

      node.textContent = target.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      node.setAttribute('datetime', target.toISOString().slice(0, 10));
    }
  }

  /**
   * Reveal the sticky bar once the main buy button has scrolled out of view.
   */
  #initStickyBar() {
    const bar = this.querySelector('[data-cpdp-bar]');
    const anchor = this.querySelector('[data-cpdp-atc]');
    if (!bar || !anchor) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        // Direction matters. `!isIntersecting` alone is also true while the
        // button is still below the fold, which is the whole of the page top —
        // so the bar used to greet the customer on arrival. Checking that the
        // button sits above the viewport limits it to genuinely scrolled past.
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.setAttribute('data-visible', String(scrolledPast));
      },
      { threshold: 0 }
    );
    this.observer.observe(anchor);
  }
}

if (!customElements.get('conversion-pdp')) {
  customElements.define('conversion-pdp', ConversionPdp);
}
